import isFinite from 'lodash/isFinite';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { formatToday } from '../../../utils/date';
import { queryHashGenerator } from '../../../utils/queryHash';
import { scaleZScore, zScore } from '../../../utils/scoring';
import { testMetaString } from '../../../utils/testMetaString';
import {
  EntityType,
  EZScorePreference,
  IAggregateWithMetric,
  ICrossTestMetricSelection,
  ISelectableEntity,
  ITag,
} from '../../types';
import {
  crossTestAggregates,
  ICrossTestAggregates,
} from '../crossTestAggregatesProvider';
import { IComparisonPopulationSetting } from '../settings/comparisonPopulationSettingsProvider';
import {
  IProfileReportSettings,
  IProfileReportSettingsState,
  profileReportSettingsProvider,
} from '../settings/reports/profileReportSettingsProvider';
import { ISettingsTemplate } from '../settings/reports/types';
import { calculateTsa } from '../utils/calculateTsa';
import { determinePopulation } from '../utils/determinePopulation';
import {
  convertTimeframeToLocalTime,
  formatTimeframe,
} from '../utils/formatTimeframe';
import { gatherQueryHashes } from '../utils/gatherQueryHashes';
import { getSessionRangeTimestamps } from '../utils/getSessionRangeTimestamps';
import { isValidDateRange } from '../utils/isValidDateRange';
import { IReportState } from './types';

export interface IProfileResult {
  metric: ICrossTestMetricSelection;
  target: IAggregateWithMetric;
  targetTimeframe: string;
  targetCount: number;
  population: IAggregateWithMetric;
  populationTimeframe: string;
  populationCount: number;
  zscore: number;
  change: number;
  percentDifference: number;
  testMeta: string;
  label: string;
  populationScore: number;
  units: string;
  roundingPrecision: number;
}

export interface IProfileReportState
  extends IReportState,
    IProfileReportSettingsState {
  data: IProfileResult[];
  settings: ISettingsTemplate<IProfileReportSettings>;
  tsa: number;
}

/**
 * @description pipeline that observes the profile data requested based on the profile report settings
 * 	and then determines and emits the state of the report
 * @param entityType
 * @param entityId
 * @returns
 */
export const profileReportProvider = (
  entityType: EntityType,
  entityId: string
): Observable<IProfileReportState> => {
  return profileReportSettingsProvider().pipe(
    /// observe the target aggregates as defined in the settings
    switchMap(
      (
        settings: IProfileReportSettingsState
      ): Observable<[IProfileReportSettingsState, ICrossTestAggregates]> => {
        const { targetTimeframe } = settings;
        const profileSettingsValidation = validateSettings(settings);
        if (profileSettingsValidation.isValid) {
          /// gather the list of unique query hashes
          const queryHashes = gatherQueryHashes(
            settings?.selected?.settings?.metrics
          );
          return combineLatest([
            of<IProfileReportSettingsState>(settings),
            crossTestAggregates(
              queryHashes,
              entityId,
              entityType,
              targetTimeframe.dateType,
              targetTimeframe.dateRange
            ),
          ]);
        } else {
          return of([settings, null]);
        }
      }
    ),
    switchMap(
      (
        profileResults: [IProfileReportSettingsState, ICrossTestAggregates]
      ): Observable<
        [
          IProfileReportSettingsState,
          ICrossTestAggregates,
          ICrossTestAggregates
        ]
      > => {
        const [settings, profileAggregates] = profileResults;
        const profileSettingsValidation = validateSettings(settings);
        if (profileSettingsValidation.isValid) {
          /// gather the list of unique query hashes
          const queryHashes = gatherQueryHashes(
            settings?.selected?.settings?.metrics
          );
          const [populationEntityType, populationEntityId] =
            determinePopulation(settings?.comparisonPopulation?.entity, {
              entityId,
              entityType,
            });

          const afterData =
            settings.targetTimeframe?.dateType?.value === 'all-time'
              ? null
              : profileAggregates;

          return combineLatest([
            of<IProfileReportSettingsState>(settings),
            of<ICrossTestAggregates>(profileAggregates),
            crossTestAggregates(
              queryHashes,
              populationEntityId,
              populationEntityType as EntityType,
              settings?.comparisonPopulation?.dateRangeType,
              settings?.comparisonPopulation?.dateRangeValues,
              afterData
            ),
          ]);
        } else {
          return combineLatest([
            of<IProfileReportSettingsState>(settings),
            of<ICrossTestAggregates>(profileAggregates),
            of(null),
          ]);
        }
      }
    ),
    /// map the aggregate data to a state
    map(
      (
        data: [
          IProfileReportSettingsState,
          ICrossTestAggregates,
          ICrossTestAggregates
        ]
      ): IProfileReportState => {
        const settingsValidation = validateSettings(data[0]);
        if (settingsValidation.isValid) {
          const targetSettings = data[0].selected.settings;
          const populationSettings = data[0].comparisonPopulation;
          const results = makeProfileResults(
            targetSettings,
            data[1],
            populationSettings,
            data[2]
          );

          /// validate the data
          const dataValidation = validateData(results);
          const localComparisonTimeframe = convertTimeframeToLocalTime({
            dateType: data[0]?.comparisonPopulation?.dateRangeType,
            dateRange: data[0]?.comparisonPopulation?.dateRangeValues,
          });

          return {
            ...dataValidation,
            ...data[0],
            comparisonPopulation: {
              entity: data[0]?.comparisonPopulation?.entity,
              dateRangeValues: localComparisonTimeframe.dateRange,
              dateRangeType: localComparisonTimeframe.dateType,
              zScorePreference: data[0]?.comparisonPopulation?.zScorePreference,
            },
            targetTimeframe: convertTimeframeToLocalTime(
              data[0]?.targetTimeframe
            ),
            data: results,
            timeframe: formatToday(),
            settings: data[0]?.selected,
            tsa: calculateTsa(results),
          };
        } else {
          return {
            ...settingsValidation,
            ...data[0],
            data: [],
            timeframe: formatToday(),
            settings: data[0]?.selected,
            tsa: null,
          };
        }
      }
    )
  );
};

/**
 * @description generates the individual profile result for each metric defined in the report settings
 * @param settings
 * @param aggregates
 * @returns
 */
const makeProfileResults = (
  profileSettings: IProfileReportSettings,
  targetAggregates: ICrossTestAggregates,
  populationSettings: IComparisonPopulationSetting,
  populationAggregates: ICrossTestAggregates
): IProfileResult[] => {
  return profileSettings?.metrics?.map(
    (selection: ICrossTestMetricSelection): IProfileResult => {
      const queryHash = queryHashGenerator(
        selection.testType?.value,
        selection?.tags?.map((se: ISelectableEntity): string => se.value) ?? []
      );
      const targetData = targetAggregates?.[queryHash]?.combined?.data?.[
        selection?.metric?.value
      ] as IAggregateWithMetric;
      const populationData =
        populationAggregates?.[queryHash]?.combined?.data?.[
          selection?.metric?.value
        ];
      const testMeta = testMetaString(
        selection.testType?.label,
        selection?.tags?.map(
          (se: ISelectableEntity): ITag => se.entity as ITag
        ) ?? []
      );
      const { start: startA, end: endA } = getSessionRangeTimestamps(
        targetAggregates?.[queryHash]?.sessions ?? []
      );
      const { start: startB, end: endB } = getSessionRangeTimestamps(
        populationAggregates?.[queryHash]?.sessions ?? []
      );
      /// make sure both population and target have a mean
      if (isFinite(targetData?.mean) && isFinite(populationData?.mean)) {
        const meanA = targetData.mean;
        const meanB = populationData.mean;
        const total = meanA + meanB;

        const change = meanA - meanB;

        const zscore = zScore(
          meanA,
          meanB,
          populationData?.populationSd ?? 0,
          targetData?.direction
        );

        return {
          metric: selection,
          target: targetData,
          targetTimeframe: formatTimeframe(startA, endA),
          targetCount: targetData?.count ?? 0,
          population: populationData,
          populationTimeframe: formatTimeframe(startB, endB),
          populationCount: populationData?.count ?? 0,
          zscore:
            populationSettings.zScorePreference === EZScorePreference.Scaled
              ? scaleZScore(zscore)
              : zscore,
          change,
          percentDifference: (change / (total / 2)) * 100,
          testMeta,
          label: `${testMeta}:${selection.metric.label}`,
          populationScore:
            populationSettings.zScorePreference === EZScorePreference.Scaled
              ? 50
              : 0,
          units: targetData?.units ? targetData?.units : populationData?.units,
          roundingPrecision: targetData?.roundingPrecision
            ? targetData?.roundingPrecision
            : populationData?.roundingPrecision,
        };
      } else {
        /// result cannot be calculated because either the target
        /// or the population do not have a finite value for the mean
        return {
          metric: selection,
          target: targetData,
          targetTimeframe: formatTimeframe(startA, endA),
          targetCount: targetData?.count ?? 0,
          population: populationData,
          populationTimeframe: formatTimeframe(startB, endB),
          populationCount: populationData?.count ?? 0,
          zscore: null,
          change: null,
          percentDifference: null,
          testMeta,
          label: `${testMeta}:${selection.metric.label}`,
          populationScore:
            populationSettings.zScorePreference === EZScorePreference.Scaled
              ? 50
              : 0,
          units: targetData?.units ? targetData?.units : populationData?.units,
          roundingPrecision: targetData?.roundingPrecision
            ? targetData?.roundingPrecision
            : populationData?.roundingPrecision,
        };
      }
    }
  );
};

interface IValidationResult {
  isValid: boolean;
  errorCode?: string;
}

/**
 * @description validates the user settings for the report
 * @param settings
 * @returns
 */
const validateSettings = (
  settings: IProfileReportSettingsState
): IValidationResult => {
  /// check to make sure a template is selected
  if (!settings?.selected) {
    return {
      isValid: false,
      errorCode: 'missing-template',
    };
  }
  /// check to make sure metrics are defined
  if (!(settings?.selected?.settings?.metrics?.length > 0)) {
    return {
      isValid: false,
      errorCode: 'missing-metrics',
    };
  }
  /// check to make sure data set A is valid
  if (!validTarget(settings)) {
    return {
      isValid: false,
      errorCode: 'invalid-target',
    };
  }

  return { isValid: true };
};

/**
 * @description determines if the settings for the profile target are valid
 * @param settings
 * @returns
 */
const validTarget = (settings: IProfileReportSettingsState): boolean => {
  if (
    !isValidDateRange({
      dateType: settings?.targetTimeframe?.dateType,
      dateRange: settings?.targetTimeframe?.dateRange,
    })
  ) {
    return false;
  }
  return true;
};

/**
 * @description validates that there is enough data to generate the report
 * @param results
 *
 */
const validateData = (results: IProfileResult[]): IValidationResult => {
  /// starts as invalid
  let targetDataIsValid: boolean = false;
  let populationDataIsValid: boolean = false;
  for (const result of results) {
    if (!targetDataIsValid) {
      targetDataIsValid = result.targetCount > 0;
    }
    if (!populationDataIsValid) {
      populationDataIsValid = result.populationCount > 0;
    }
  }

  if (!targetDataIsValid) {
    return {
      isValid: false,
      errorCode: 'target-insufficient-data',
    };
  }

  if (!populationDataIsValid) {
    return {
      isValid: false,
      errorCode: 'population-insufficient-data',
    };
  }

  return {
    isValid: true,
  };
};
